import type { PhoneInputLabels, PhoneRefusalDetail } from '@repletoryreach/sdk-ui/inputs';
import { findCountry } from '@repletoryreach/sdk-utils/phone';
import type { Translate } from '../i18n/I18nProvider';

/**
 * The copy every phone field in this console shows: the "Will be saved as"
 * line, the picker's search box, and one sentence per refusal reason. The
 * rule that decides those reasons lives in `@repletoryreach/sdk-utils/phone`
 * and is the same one the services run.
 */
export function phoneLabels(t: Translate): PhoneInputLabels {
  return {
    savedAs: (formatted) => t('phone.savedAs', { number: formatted }),
    search: t('phone.search'),
    noMatches: t('phone.noMatches'),
    country: t('phone.country'),
    reasons: {
      empty: () => t('phone.reason.empty'),
      not_a_number: () => t('phone.reason.notANumber'),
      too_short: (d) => t('phone.reason.tooShort', lengthVars(d)),
      too_long: (d) => t('phone.reason.tooLong', lengthVars(d)),
      unknown_country: () => t('phone.reason.unknownCountry'),
      no_country: () => t('phone.reason.noCountry')
    }
  };
}

function lengthVars(d: PhoneRefusalDetail): Record<string, string> {
  return { country: d.country?.name ?? '', dial: d.country?.dial ?? '', expected: d.expected ?? '' };
}

/**
 * The service's refusal of a number, in this console's words. The 400 carries
 * `reason`, `assumed_country` and `expected_digits`, the same facts the field
 * uses, so the sentence is the one the person already saw under the field.
 * Anything else comes back null so the caller can fall through to its usual
 * error handling.
 */
export function phoneRefusalText(err: unknown, t: Translate): string | null {
  const e = err as { status?: number; body?: { error?: { code?: string; reason?: string; assumed_country?: string | null; expected_digits?: string | null; message?: string } } } | null;
  const error = e?.body?.error;
  if (!e || e.status !== 400 || !error || typeof error.reason !== 'string') return null;
  if (error.code !== 'PHONE_NUMBER_INVALID' && error.code !== 'BAD_NUMBER') return null;
  const country = findCountry(error.assumed_country);
  const detail: PhoneRefusalDetail = { country, expected: error.expected_digits ?? undefined };
  const reasons = phoneLabels(t).reasons ?? {};
  const say = reasons[error.reason as keyof typeof reasons];
  const text = say ? say(detail) : error.message;
  return typeof text === 'string' ? text : (error.message ?? null);
}
